import React, { useEffect, useMemo, useCallback, useState } from 'react';
import { useObserver } from 'mobx-react';
import { groupBy } from 'lodash';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import useCourtFormsSearch from '~/src/hooks/useCourtFormsSearch';
import usePageScroll from '~/src/hooks/usePageScroll';
import useMst from '~/src/hooks/useMst';
import usePdfCourtFormsActions from '~/src/hooks/usePdfCourtFormsActions';
import LoadMoreDisplayText from '~/src/components/LoadMoreDisplayText';
import Table from '~/src/containers/Table';
import { ReactComponent as InformationIcon } from './information-icon.svg';
import { REQUEST_COURT_FORM_URL } from '~/src/constants/externalLinks';
import {
  PdfTableRowAction,
  type PdfCourtForm,
} from '~/src/components/CourtForms/PdfTableWrapper';
import { stateCodeToStateName } from '~/src/utils/stateCodeToStateName';
import { TOTAL_DOCUMENT_LIMIT } from '~/src/utils/constants';
import type { Document } from '~/src/services/pdfCourtFormsService';

interface CategoryGroup {
  category: string;
  items: Document[];
}

interface RegionGroup {
  region: string;
  label: string;
  categories: CategoryGroup[];
}

const REGION_LABELS: Record<string, string> = {
  ca: 'Canada Forms',
  us: 'US Forms',
  au: 'Australia Forms',
  eu: 'EU Forms',
};

const isValidTagValue = (value: string | undefined | null): value is string =>
  !!value && value !== 'None';

const getTag = (
  doc: Document,
): { tag: string | null; tagLabel: string | null } => {
  if (doc.region === 'us' || doc.region === 'ca') {
    const locality = isValidTagValue(doc.locality) ? doc.locality : null;
    const label = doc.region === 'ca' ? 'Provider' : 'County';
    return { tag: locality, tagLabel: locality ? label : null };
  }
  const practiceArea = isValidTagValue(doc.legal_practice_area)
    ? doc.legal_practice_area
    : null;
  return {
    tag: practiceArea,
    tagLabel: practiceArea ? 'Practice Area' : null,
  };
};

const getCategoryDisplayName = (category: string, region: string): string => {
  if (region === 'us' && category in stateCodeToStateName) {
    return stateCodeToStateName[category as keyof typeof stateCodeToStateName];
  }
  return category;
};

const FormNumberCell: React.FC<{ doc: Document }> = ({ doc }) => {
  const { tag, tagLabel } = getTag(doc);
  return (
    <div>
      <div className="text-sm font-medium text-gray-900 truncate">
        {doc.title}
      </div>
      {tag && (
        <span className="inline-flex items-center mt-0.5 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 whitespace-nowrap">
          {tagLabel}: {tag}
        </span>
      )}
    </div>
  );
};

const categoryKey = (region: string, category: string) =>
  `${region}:${category}`;

const toggleInSet = <T,>(set: Set<T>, key: T): Set<T> => {
  const next = new Set(set);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  return next;
};

const CollapseToggle: React.FC<{
  collapsed: boolean;
  onToggle: () => void;
  label: string;
  className: string;
  iconSize: number;
}> = ({ collapsed, onToggle, label, className, iconSize }) => (
  <button
    type="button"
    onClick={onToggle}
    aria-expanded={!collapsed}
    aria-label={`${collapsed ? 'Expand' : 'Collapse'} ${label}`}
    className={`flex items-center w-full text-left ${className}`}
  >
    <ChevronRightIcon
      style={{
        fontSize: iconSize,
        transform: `rotate(${collapsed ? 0 : 90}deg)`,
        transition: 'transform 0.2s ease-in-out',
      }}
    />
    <span className="ml-2">{label}</span>
  </button>
);

const CourtFormsSearchResults: React.FC<{ query: string }> = ({ query }) => {
  const { results, loading, done, loadMore } = useCourtFormsSearch(query);
  const pageScroll = usePageScroll();
  const [collapsedRegions, setCollapsedRegions] = useState<Set<string>>(
    () => new Set(),
  );
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(
    () => new Set(),
  );

  const toggleRegion = (region: string) =>
    setCollapsedRegions((prev) => toggleInSet(prev, region));

  const toggleCategory = (region: string, category: string) =>
    setCollapsedCategories((prev) =>
      toggleInSet(prev, categoryKey(region, category)),
    );

  const { sidebarItemTotalNumber, containedInSidebar } = useMst((store) =>
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useObserver(() => ({
      sidebarItemTotalNumber: store.sidebarItems.items.length,
      containedInSidebar: store.sidebarItems.containedInSidebar,
    })),
  );

  const {
    addSearchResultToSelectionSidebar: handleAddTemplate,
    previewSearchResult: handlePreviewTemplate,
    createFormTemplate: handleCreateFromTemplate,
  } = usePdfCourtFormsActions();

  const disableAddButton = sidebarItemTotalNumber >= TOTAL_DOCUMENT_LIMIT;

  const rowActions = useMemo(
    () => ({
      previewFormTemplate: handlePreviewTemplate,
      createFormTemplate: handleCreateFromTemplate,
    }),
    [handlePreviewTemplate, handleCreateFromTemplate],
  );

  useEffect(() => {
    setCollapsedRegions(new Set());
    setCollapsedCategories(new Set());
  }, [query]);

  useEffect(() => {
    if (results.length > 0 && pageScroll >= 85 && !loading && !done) {
      loadMore();
    }
  }, [pageScroll, results.length, loading, done, loadMore]);

  useEffect(() => {
    if (results.length === 0 || loading || done) return undefined;
    const frame = window.requestAnimationFrame(() => {
      const el = document.getElementById('lawyaw-main-body');
      if (!el) return;
      if (el.clientHeight / (el.scrollHeight - el.scrollTop) >= 0.85) {
        loadMore();
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [
    collapsedRegions,
    collapsedCategories,
    results.length,
    loading,
    done,
    loadMore,
  ]);

  const regionGroups = useMemo<RegionGroup[]>(() => {
    const byRegion = groupBy(results, (doc) => doc.region || 'other');
    return Object.entries(byRegion).map(([region, docs]) => {
      const byCategory = groupBy(docs, (doc) => doc.filter_category || 'Other');
      const categories = Object.entries(byCategory)
        .map(([category, items]) => ({ category, items }))
        .sort((a, b) => a.category.localeCompare(b.category));
      return {
        region,
        label: REGION_LABELS[region] || region.toUpperCase(),
        categories,
      };
    });
  }, [results]);

  const renderRowAction = useCallback(
    (doc: Document) => (
      <PdfTableRowAction
        template={doc as unknown as PdfCourtForm}
        rowActions={rowActions}
        containedInSidebar={containedInSidebar}
        handleAddTemplateToSeletionSidebar={handleAddTemplate}
        handlePreviewTemplate={handlePreviewTemplate}
        disabled={disableAddButton}
      />
    ),
    [
      rowActions,
      containedInSidebar,
      handleAddTemplate,
      handlePreviewTemplate,
      disableAddButton,
    ],
  );

  const columns = useMemo(
    () => [
      {
        label: 'Form number',
        render: (doc: Document) => <FormNumberCell doc={doc} />,
        style: {
          width: '30%',
          textAlign: 'left',
          textOverflow: 'ellipsis',
          overflow: 'hidden',
        },
      },
      {
        label: 'Form title',
        dataKey: 'subtitle',
        style: {
          width: '40%',
          textAlign: 'left',
          textOverflow: 'ellipsis',
          overflow: 'hidden',
        },
      },
      {
        label: 'Actions',
        hideLabel: true,
        render: renderRowAction,
        style: { flexGrow: 1, display: 'flex', justifyContent: 'flex-end' },
      },
    ],
    [renderRowAction],
  );

  if (!loading && done && results.length === 0) {
    return (
      <div className="text-center mt-12 text-lg text-gray-500">
        No results found for &ldquo;{query}&rdquo;.
      </div>
    );
  }

  return (
    <div className="mt-4 max-w-[1080px] mx-auto">
      {regionGroups.map((regionGroup) => {
        const regionCollapsed = collapsedRegions.has(regionGroup.region);
        return (
          <div key={regionGroup.region} className="mb-8">
            <h2 className="border-b border-gray-300">
              <CollapseToggle
                collapsed={regionCollapsed}
                onToggle={() => toggleRegion(regionGroup.region)}
                label={regionGroup.label}
                className="text-lg font-bold text-gray-900 px-4 py-3"
                iconSize={20}
              />
            </h2>
            {!regionCollapsed &&
              regionGroup.categories.map((catGroup) => {
                const categoryCollapsed = collapsedCategories.has(
                  categoryKey(regionGroup.region, catGroup.category),
                );
                return (
                  <div
                    key={catGroup.category}
                    className="mb-4 pb-3 border-b border-gray-200"
                  >
                    <h3 className="bg-gray-100">
                      <CollapseToggle
                        collapsed={categoryCollapsed}
                        onToggle={() =>
                          toggleCategory(regionGroup.region, catGroup.category)
                        }
                        label={getCategoryDisplayName(
                          catGroup.category,
                          regionGroup.region,
                        )}
                        className="text-base font-semibold text-gray-800 pl-7 pr-4 py-2"
                        iconSize={18}
                      />
                    </h3>
                    {!categoryCollapsed && (
                      <div className="mt-3 pl-9 pr-6">
                        <Table
                          emptyTitle=""
                          empty={false}
                          isLoading={false}
                          data={catGroup.items}
                          visibleItems={catGroup.items.length}
                          columns={columns}
                          isSortByDisabled
                          hideHeader
                        />
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        );
      })}
      <LoadMoreDisplayText
        loading={loading}
        done={done}
        onLoadMore={loadMore}
      />
      <div className="mt-2">
        <InformationIcon className="mr-2 mb-1" />
        <span className="mr-2 hidden lg:inline">
          Can&apos;t find the forms you are looking for?
        </span>
        <a href={REQUEST_COURT_FORM_URL} target="_blank" rel="noreferrer">
          Request a form
        </a>
      </div>
    </div>
  );
};

export default CourtFormsSearchResults;
